// src/pages/EditInvoice.jsx
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { 
  getInvoice, getAllCustomers, getAllInvoiceItems, updateInvoice, 
  getAllAccounts, getAllStudents, getFilteredLogs,
  subscribeCategories, DEFAULT_CATEGORIES
} from "../firebase/database";
import { theme } from "../utils/theme";
import { HHMMToMinutes, minutesToHHMM, formatDate, todayStr } from "../utils/helpers";

const C = theme.colors;

export default function EditInvoice() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [allPeople, setAllPeople] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [customerFilter, setCustomerFilter] = useState("business");
  
  // Log selection states
  const [allRecentLogs, setAllRecentLogs] = useState([]);
  const [logFilterStaffId, setLogFilterStaffId] = useState("");
  const [logFilterDate, setLogFilterDate] = useState(todayStr());
  const [logFilterRole, setLogFilterRole] = useState("staff");
  const [logFilterCategoryId, setLogFilterCategoryId] = useState("");
  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [fetchingLogs, setFetchingLogs] = useState(false);

  // Modal States
  const [showLogModal, setShowLogModal] = useState(false);

  const [formData, setFormData] = useState({
    customerId: "",
    customerName: "",
    staffId: "",
    staffName: "",
    date: new Date().toISOString().split("T")[0],
    status: "pending",
    paymentMethod: "",
    items: [],
    linkedLogs: [],
  });

  const [newItem, setNewItem] = useState({
    itemId: "",
    itemName: "",
    description: "",
    quantity: 1,
    rate: 0,
    total: 0,
  });

  useEffect(() => {
    loadData();
    const unsubCats = subscribeCategories(setCategories);
    return () => { if (unsubCats) unsubCats(); };
  }, [invoiceId]);

  const loadData = async () => {
    try {
      const [invoice, customersData, itemsData, accountsData, studentsData] = await Promise.all([
        getInvoice(invoiceId),
        getAllCustomers(),
        getAllInvoiceItems(),
        getAllAccounts(),
        getAllStudents(),
      ]);
      
      setCustomers(customersData);
      setItems(itemsData);
      setAccounts(accountsData.filter(a => a.nature === "Cash" || a.nature === "Bank"));
      setAllPeople(studentsData);
      
      if (invoice) {
        setFormData({
          customerId: invoice.customerId || "",
          customerName: invoice.customerName || "",
          staffId: invoice.staffId || "",
          staffName: invoice.staffName || "",
          date: invoice.date || new Date().toISOString().split("T")[0],
          status: invoice.status || "pending",
          paymentMethod: invoice.paymentMethod || "",
          items: invoice.items || [],
          linkedLogs: invoice.linkedLogs || [],
        });
        setSelectedLogIds((invoice.linkedLogs || []).map(l => l.entryId));
      }
    } catch (err) {
      console.error("Error loading data:", err);
    } finally {
      setInitialLoading(false);
    }
  };

  const handleFetchLogs = async () => {
    setFetchingLogs(true);
    try {
      const logs = await getFilteredLogs({
        studentId: logFilterStaffId,
        date: logFilterDate,
      });
      setAllRecentLogs(logs);
    } catch (err) {
      console.error("Error fetching logs:", err);
      alert(`Failed to pull logs. Please check your internet.`);
    } finally {
      setFetchingLogs(false);
    }
  };

  const handleCustomerChange = (e) => {
    const id = e.target.value;
    const person = [...customers, ...allPeople].find(p => p.id === id);
    setFormData(prev => ({
      ...prev,
      customerId: id,
      customerName: person?.name || "",
    }));
  };

  const handleItemChange = (e) => {
    const itemId = e.target.value;
    const item = items.find(i => i.id === itemId);
    if (item) {
      setNewItem(prev => ({
        ...prev,
        itemId,
        itemName: item.name,
        description: item.description || "",
        rate: item.rate || 0,
        total: (prev.quantity || 1) * (item.rate || 0),
      }));
    }
  };

  const handleQuantityChange = (qty) => {
    const quantity = parseFloat(qty) || 0;
    setNewItem(prev => ({ ...prev, quantity, total: quantity * prev.rate }));
  };

  const handleRateChange = (rate) => {
    const newRate = parseFloat(rate) || 0;
    setNewItem(prev => ({ ...prev, rate: newRate, total: prev.quantity * newRate }));
  };

  const handleAddItem = () => {
    if (!newItem.itemId || newItem.rate <= 0) {
      alert("Please select an item");
      return;
    }
    setFormData(prev => ({ ...prev, items: [...prev.items, { ...newItem }] }));
    setNewItem({ itemId: "", itemName: "", description: "", quantity: 1, rate: 0, total: 0 });
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const calculateTotal = () => formData.items.reduce((sum, item) => sum + (item.total || 0), 0);

  const totalMinutes = useMemo(() => {
    return formData.linkedLogs.reduce((sum, log) => sum + (HHMMToMinutes(log.endTime) - HHMMToMinutes(log.startTime)), 0);
  }, [formData.linkedLogs]);

  const valuePerHour = useMemo(() => {
    const total = calculateTotal();
    const hours = totalMinutes / 60;
    return hours > 0 ? total / hours : 0;
  }, [formData.items, totalMinutes]);

  const handleStatusChange = (status) => {
    setFormData(prev => ({
      ...prev,
      status,
      paymentMethod: status === "paid" ? prev.paymentMethod : "",
    }));
  };

  // Log Picker Logic
  const logsWithNames = useMemo(() => {
    return allRecentLogs.map(log => {
      const person = allPeople.find(s => s.id === log.studentId);
      return {
        ...log,
        studentName: person?.name || log.studentName || "Member",
        role: person?.role || "student"
      };
    });
  }, [allRecentLogs, allPeople]);

  const filteredLogsForPicking = useMemo(() => {
    return logsWithNames.filter(log => {
      if (!logFilterStaffId && logFilterRole === "staff") {
        const isStaff = ["staff", "trainer", "instructor", "admin"].includes(log.role);
        if (!isStaff) return false;
      }
      if (logFilterCategoryId && log.category !== logFilterCategoryId) return false;
      const matchesStaff = !logFilterStaffId || log.studentId === logFilterStaffId;
      const matchesDate = !logFilterDate || log.date === logFilterDate;
      const notAlreadySelected = !selectedLogIds.includes(log.entryId);
      return matchesStaff && matchesDate && notAlreadySelected;
    });
  }, [logsWithNames, logFilterStaffId, logFilterDate, logFilterRole, logFilterCategoryId, selectedLogIds]);

  const toggleLogSelection = (log) => {
    if (selectedLogIds.includes(log.entryId)) {
      setSelectedLogIds(prev => prev.filter(id => id !== log.entryId));
      setFormData(prev => ({ ...prev, linkedLogs: prev.linkedLogs.filter(l => l.entryId !== log.entryId) }));
    } else {
      setSelectedLogIds(prev => [...prev, log.entryId]);
      const person = allPeople.find(s => s.id === log.studentId);
      const enrichedLog = { ...log, studentName: person?.name || log.studentName || "Member" };
      setFormData(prev => ({ ...prev, linkedLogs: [...prev.linkedLogs, enrichedLog] }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerId || formData.items.length === 0) {
      alert("Please select a customer and add at least one item");
      return;
    }

    setLoading(true);
    try {
      await updateInvoice(invoiceId, {
        ...formData,
        totalAmount: calculateTotal(),
        totalEffortMinutes: totalMinutes,
        valuePerHour,
      });
      navigate("/admin/invoices");
    } catch (err) {
      console.error("Error updating invoice:", err);
      alert("Error updating invoice");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(amount || 0);
  };

  if (initialLoading) return <div style={{ padding: 40, textAlign: "center", fontFamily: "DM Sans" }}>Loading Invoice Data...</div>;

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px", fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; }
        .modal-card { background: white; border-radius: 16px; width: 500px; padding: 32px; box-shadow: 0 10px 25px rgba(0,0,0,0.1); }
        .log-modal-card { width: 900px; max-height: 80vh; overflow-y: auto; }
        .form-group { marginBottom: 20px; }
        .form-label { display: block; fontSize: 15px; fontWeight: 600; color: #374151; marginBottom: 8px; }
        .form-input { width: 100%; padding: 12px 16px; border: 1px solid #D1D5DB; borderRadius: 10px; fontSize: 16px; outline: none; box-sizing: border-box; }
        .log-item { display: flex; align-items: center; gap: 12px; padding: 12px; border-bottom: 1px solid #eee; cursor: pointer; border-radius: 8px; }
        .log-item:hover { background: #f9fafb; }
      `}</style>

      {/* Work Log Picker Modal */}
      {showLogModal && (
        <div className="modal-overlay">
          <div className="modal-card log-modal-card">
            <h3 style={{ margin: "0 0 16px 0", fontSize: 20, fontWeight: 700 }}>Link Effort / Work Logs</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 20 }}>
              <div style={{ flex: "1 1 200px" }}>
                <label className="form-label">Filter by Type</label>
                <select className="form-input" value={logFilterRole} onChange={e => setLogFilterRole(e.target.value)}>
                  <option value="staff">Staff Only</option>
                  <option value="all">All Members</option>
                </select>
              </div>
              <div style={{ flex: "1 1 200px" }}>
                <label className="form-label">Member</label>
                <select className="form-input" value={logFilterStaffId} onChange={e => setLogFilterStaffId(e.target.value)}>
                  <option value="">{logFilterRole === "staff" ? "All Staff" : "All Members"}</option>
                  {allPeople
                    .filter(s => logFilterRole === "all" || ["staff", "trainer", "instructor", "admin"].includes(s.role))
                    .map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                </select>
              </div>
              <div style={{ flex: "1 1 200px" }}>
                <label className="form-label">Category</label>
                <select className="form-input" value={logFilterCategoryId} onChange={e => setLogFilterCategoryId(e.target.value)}>
                  <option value="">All Categories</option>
                  {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.label}</option>)}
                </select>
              </div>
              <div style={{ flex: "1 1 150px" }}>
                <label className="form-label">Date</label>
                <input type="date" className="form-input" value={logFilterDate} onChange={e => setLogFilterDate(e.target.value)} />
              </div>
              <div style={{ flex: "1 1 150px", display: "flex", alignItems: "flex-end" }}>
                <button type="button" onClick={handleFetchLogs} disabled={fetchingLogs} style={{ width: "100%", height: 48, borderRadius: 10, background: "#3B82F6", color: "white", fontWeight: 700, cursor: "pointer" }}>
                  {fetchingLogs ? "..." : "Pull Logs"}
                </button>
              </div>
            </div>

            <div style={{ maxHeight: 400, overflowY: "auto", border: "1px solid #eee", borderRadius: 12, padding: 8 }}>
              {filteredLogsForPicking.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>No matching logs found.</div>
              ) : (
                filteredLogsForPicking.map(log => (
                  <div key={log.entryId} className="log-item" onClick={() => toggleLogSelection(log)}>
                    <input type="checkbox" checked={selectedLogIds.includes(log.entryId)} readOnly />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{log.studentName} - {log.topicName} {log.notes && <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 4 }}> ({log.notes})</span>}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{formatDate(log.date)} | {log.startTime}-{log.endTime} ({minutesToHHMM(HHMMToMinutes(log.endTime) - HHMMToMinutes(log.startTime))}) | Category: {log.category}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
            <button type="button" onClick={() => setShowLogModal(false)} style={{ width: "100%", marginTop: 24, padding: "12px", borderRadius: 10, background: "#111827", color: "white", border: "none", fontWeight: 700, cursor: "pointer" }}>Done Linking</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 40, display: "flex", alignItems: "center", gap: 16 }}>
        <Link to="/admin/invoices" style={{ color: "#6B7280", background: "#f3f4f6", padding: 12, borderRadius: "50%" }}>
          <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" /></svg>
        </Link>
        <div>
          <h1 style={{ fontSize: 32, fontWeight: 800, color: "#111827", margin: 0 }}>Edit Invoice</h1>
          <p style={{ margin: "4px 0 0 0", fontSize: 15, color: "#6b7280" }}>Updating Transaction #{invoiceId}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
          
          {/* Section 1: Customer & Status */}
          <div style={{ background: "white", borderRadius: 20, border: "1px solid #E5E7EB", padding: 32, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 32 }}>
              <div>
                <label className="form-label" style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Bill To *</span>
                  <div style={{ display: "flex", gap: 4, background: "#f3f4f6", padding: 3, borderRadius: 8 }}>
                    {["business", "student", "all"].map(f => (
                      <button key={f} type="button" onClick={() => setCustomerFilter(f)} style={{ padding: "2px 8px", borderRadius: 6, border: "none", fontSize: 10, fontWeight: 700, cursor: "pointer", background: customerFilter === f ? "white" : "transparent", color: customerFilter === f ? "#3B82F6" : "#6B7280" }}>{f.toUpperCase()}</button>
                    ))}
                  </div>
                </label>
                <select value={formData.customerId} onChange={handleCustomerChange} required className="form-input">
                  <option value="">Select Person</option>
                  {(customerFilter === "business" || customerFilter === "all") && (
                    <optgroup label="Business Customers">{customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>
                  )}
                  {(customerFilter === "student" || customerFilter === "all") && (
                    <optgroup label="Students">{allPeople.map(s => <option key={s.id} value={s.id}>{s.name} (Roll: {s.roll})</option>)}</optgroup>
                  )}
                </select>
              </div>
              <div>
                <label className="form-label">Invoice Date</label>
                <input type="date" value={formData.date} onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))} className="form-input" />
              </div>
              <div>
                <label className="form-label">Status</label>
                <select value={formData.status} onChange={(e) => handleStatusChange(e.target.value)} className="form-input" style={{ background: formData.status === "paid" ? "#f0fdf4" : "#fff7ed", color: formData.status === "paid" ? "#166534" : "#9a3412", fontWeight: 700 }}>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
              {formData.status === "paid" && (
                <div>
                  <label className="form-label">Deposit To</label>
                  <select value={formData.paymentMethod} onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))} className="form-input">
                    <option value="">Select Account</option>
                    {accounts.map(a => (<option key={a.id} value={a.name}>{a.name}</option>))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Items */}
          <div style={{ background: "white", borderRadius: 20, border: "1px solid #E5E7EB", padding: 32 }}>
            <h3 style={{ margin: "0 0 24px 0", fontSize: 20, fontWeight: 700 }}>Items & Services</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1.5fr 80px 120px 120px auto", gap: 12, marginBottom: 32, background: "#f9fafb", padding: 20, borderRadius: 16 }}>
              <select value={newItem.itemId} onChange={handleItemChange} className="form-input"><option value="">Choose item...</option>{items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}</select>
              <input type="number" value={newItem.quantity} onChange={e => handleQuantityChange(e.target.value)} className="form-input" />
              <input type="number" value={newItem.rate} onChange={e => handleRateChange(e.target.value)} className="form-input" />
              <div style={{ padding: "12px", background: "#e5e7eb", borderRadius: 10, fontWeight: 800 }}>₹{(newItem.quantity * newItem.rate).toFixed(2)}</div>
              <button type="button" onClick={handleAddItem} style={{ height: 48, padding: "0 20px", background: "#111827", color: "white", border: "none", borderRadius: 10, fontWeight: 700, cursor: "pointer" }}>Add</button>
            </div>

            {formData.items.length > 0 && (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ background: "#F9FAFB", borderBottom: "2px solid #E5E7EB" }}><th style={{ padding: 12, textAlign: "left" }}>Item</th><th style={{ padding: 12 }}>Qty x Rate</th><th style={{ padding: 12, textAlign: "right" }}>Total</th><th style={{ padding: 12 }}>Action</th></tr></thead>
                <tbody>
                  {formData.items.map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: 12 }}><strong>{item.itemName}</strong></td>
                      <td style={{ padding: 12, textAlign: "center" }}>{item.quantity} × ₹{item.rate}</td>
                      <td style={{ padding: 12, textAlign: "right" }}><strong>₹{item.total.toFixed(2)}</strong></td>
                      <td style={{ padding: 12, textAlign: "center" }}><button type="button" onClick={() => handleRemoveItem(idx)} style={{ color: "#ef4444", border: "none", background: "none", cursor: "pointer" }}>Remove</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Section 3: Logs */}
          <div style={{ background: "white", borderRadius: 20, border: "1px solid #E5E7EB", padding: 32 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Effort Tracking (Linked Logs)</h3>
              <button type="button" onClick={() => setShowLogModal(true)} style={{ background: "#3B82F6", color: "white", border: "none", padding: "10px 20px", borderRadius: 10, fontWeight: 700, cursor: "pointer" }}>+ Link Logs</button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
              {formData.linkedLogs.map(log => (
                <div key={log.entryId} style={{ padding: 16, background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, position: "relative" }}>
                  <button type="button" onClick={() => toggleLogSelection(log)} style={{ position: "absolute", top: 8, right: 8, color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontSize: 18 }}>×</button>
                  <div style={{ fontWeight: 700 }}>{log.studentName}</div>
                  <div style={{ fontSize: 13, color: "#374151", margin: "4px 0" }}>{log.topicName} {log.notes && <span style={{ fontSize: 12, color: "#6b7280", fontStyle: "italic" }}>- {log.notes}</span>}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{formatDate(log.date)} | {log.startTime}-{log.endTime} ({minutesToHHMM(HHMMToMinutes(log.endTime)-HHMMToMinutes(log.startTime))})</div>
                </div>
              ))}
            </div>

            {formData.linkedLogs.length > 0 && (
              <div style={{ marginTop: 24, padding: 20, background: "#f0fdf4", borderRadius: 12, display: "flex", gap: 40 }}>
                 <div>
                    <div style={{ fontSize: 12, color: "#166534", fontWeight: 700, textTransform: "uppercase" }}>Total Effort</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: "#166534" }}>{minutesToHHMM(totalMinutes)} <span style={{ fontSize: 14, fontWeight: 500 }}>({(totalMinutes/60).toFixed(2)} hrs)</span></div>
                 </div>
                 <div>
                    <div style={{ fontSize: 12, color: "#166534", fontWeight: 700, textTransform: "uppercase" }}>Task Value per Hour</div>
                    <div style={{ fontSize: 20, fontWeight: 800, color: "#166534" }}>{formatCurrency(valuePerHour)}/hr</div>
                 </div>
              </div>
            )}
          </div>

          {/* Footer Summary */}
          <div style={{ background: "#111827", borderRadius: 24, padding: 40, color: "white", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center" }}>
              <div>
                <h3 style={{ margin: "0 0 24px 0", fontSize: 16, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "1px" }}>Payment Summary</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16 }}>
                    <span style={{ color: "#9ca3af" }}>Items Subtotal</span>
                    <span style={{ fontWeight: 600 }}>{formatCurrency(calculateTotal())}</span>
                  </div>
                  {totalMinutes > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16 }}>
                      <span style={{ color: "#9ca3af" }}>Total Effort (Hrs)</span>
                      <span style={{ fontWeight: 600 }}>{(totalMinutes/60).toFixed(2)} hrs</span>
                    </div>
                  )}
                  <div style={{ height: 1, background: "#374151", margin: "8px 0" }} />
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 18, fontWeight: 600 }}>Grand Total</span>
                    <span style={{ fontSize: 36, fontWeight: 900, color: "#3B82F6" }}>{formatCurrency(calculateTotal())}</span>
                  </div>
                </div>
              </div>
              <button type="submit" disabled={loading} style={{ width: "100%", padding: 20, background: "#3B82F6", color: "white", border: "none", borderRadius: 12, fontSize: 18, fontWeight: 800, cursor: "pointer" }}>
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
}
